/**
 * Count menu: clickable list of count types to go inside mp-drop-menu
 */
import {AttrsSchema} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChildMenu, ChildMenuBaseAttrs} from 'common/query-builder/query-block/child-menu';
import {AdvancedMenuValue, CountBy, CountType} from 'common/query-builder/query-entry-section/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {ChartType, COUNT_USERS_ONCE_TYPE, CountUsersOnceState, MATH_TYPE, PropertyType} from 'common/report/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {ChangeEvent} from 'common/components/select/types';
import {showTour, TourId} from 'common/util/chameleon';
import {DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {RollingWindowSize} from 'common/util';
import {getSelectedTimePeriodLabel} from 'common/query-builder/query-entry-section/util';

import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {IncludeCustomPropertyType, isProperty, SelectPropertyEvent} from '../properties-menu/types';
import {propertyItemToSegmentation} from '../properties-menu/util';
import {MetricType} from '../constants';
import {FUNNELS_TIME_TO_CONVERSION} from '../properties-menu/constants';
import {
  AggregationSelector,
  CountTypeSelector,
  FUNNELS_COUNT_MENU_ITEMS,
  INSIGHTS_COUNT_MENU_ITEMS,
  PROFILE_COUNT_MENU_ITEMS,
  RETENTION_COUNT_MENU_ITEMS,
  SubSelectionTypes,
} from './constants';
import {
  generateSectionItem,
  getPropertyName,
  getSelectedCountByLabel,
  getSubSelectionsForCountType,
  shouldCommitSelection,
} from './util';
import css from './index.inline.styl';
/*eslint-disable-next-line import/no-cycle*/
import {template} from './template';

interface CountMenuState {
  isSubSelectionsVisible: boolean;
  isSelectorHovered: boolean; // is selector hovered
  isPropertyMenuOpen: boolean;
  isNestedPropertyMenuOpen: boolean; // cart analysis
  isAggregationPerUserMenuOpen: boolean;
  isAggregationMenuOpen: boolean;
  isCountUsersOnceMenuOpen: boolean;
  isAdvancedMenuOpen: boolean;
  // selections
  selectedCountType: CountType;
  selectedCountTypeIndex: number;
  selectedProperty: PropertySectionItem;
  selectedAggregationOperator: MATH_TYPE;
  selectedAggregationOperatorPerUser: MATH_TYPE;
  selectedCountUsersOnceType: COUNT_USERS_ONCE_TYPE;
  countTypeSelections: Array<SectionItem<CountTypeSelector>>;
}

interface CountMenuAttrs {
  'chart-type': ChartType;
  'data-groups'?: DataGroup[];
  'date-range': DateRangeAttributes;
  'date-unit': RollingWindowSize;
  'enable-workspace-filter-restrictions'?: boolean;
  'include-custom-properties'?: IncludeCustomPropertyType;
  'advanced-menu-sections'?: Array<Section<AdvancedMenuValue>>;
  'advanced-menu-label'?: string;
  'count-users-once-state': CountUsersOnceState;
}

type CountMenuJsxProps = {
  attrs?: ChildMenuBaseAttrs<CountBy> & CountMenuAttrs;
  on?: {
    select?: (ev: SelectCountByEvent) => void;
  };
};

type SelectCountByEvent = CustomEvent<CountBy>;

export class CountMenu extends ChildMenu<CountMenuState, CountMenuAttrs, CountBy> {
  static get attrsSchema(): AttrsSchema<ChildMenuBaseAttrs<CountBy> & CountMenuAttrs> {
    return {
      ...super.attrsSchema,
      'chart-type': {
        type: `string`,
      },
      'data-groups': {
        type: `json`,
        default: [],
      },
      'date-range': {
        type: `json`,
      },
      'date-unit': {
        type: `string`,
      },
      'advanced-menu-label': {
        type: `string`,
        default: `Advanced`,
      },
      'advanced-menu-sections': {
        type: `json`,
        default: [],
      },
      'count-users-once-state': {
        type: `string`,
      },
      'enable-workspace-filter-restrictions': `boolean`,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`currentProject` as const],
      defaultState: {
        isSubSelectionsVisible: false,
        isSelectorHovered: false,
        isPropertyMenuOpen: false,
        isNestedPropertyMenuOpen: false,
        isAggregationPerUserMenuOpen: false,
        isAggregationMenuOpen: false,
        isCountUsersOnceMenuOpen: false,
        isAdvancedMenuOpen: false,
        selectedCountType: null,
        selectedCountTypeIndex: -1,
        selectedProperty: null,
        selectedAggregationOperator: null,
        selectedAggregationOperatorPerUser: null,
        selectedCountUsersOnceType: null,
        countTypeSelections: [],
      },
      helpers: {
        ...super.config.helpers,
      },
    };
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `data-group-id`) {
      this.update({
        countTypeSelections: this.getCountTypeSelections(),
      });
    }
    if (attr === `open` && newVal === ``) {
      const {countType, aggregationOperator, countUsersOnceType} = this.attr(`selected`);
      const canExpand = this.state.countTypeSelections[this.state.selectedCountTypeIndex]?.value.canExpandSubSelections(
        {
          countType,
          aggregation: aggregationOperator,
          countUsersOnceType,
        },
      );
      this.update({
        isSubSelectionsVisible: Boolean(canExpand),
      });

      showTour(TourId.InsightsNewCountMenu, true);
    }
  }

  public connectedCallback() {
    super.connectedCallback();

    const countTypeSelections = this.getCountTypeSelections();
    const {countType, aggregationOperator, aggregationOperatorPerUser, property, countUsersOnceType} = this.attr(
      `selected`,
    );
    const selectedCountTypeIndex = countTypeSelections.findIndex((option) => {
      return (
        option.value.countType === countType &&
        (property?.value !== FUNNELS_TIME_TO_CONVERSION.value ||
          option.value.label === FUNNELS_TIME_TO_CONVERSION.label)
      );
    });
    this.update({
      countTypeSelections,
      selectedCountType: countType,
      selectedCountTypeIndex,
      selectedAggregationOperator: aggregationOperator,
      selectedAggregationOperatorPerUser: aggregationOperatorPerUser,
      selectedProperty: property,
      selectedCountUsersOnceType: countUsersOnceType,
    });
  }

  /** Defines how the output copy will look like */
  public selectedLabel() {
    return getSelectedCountByLabel({
      metricType: this.attr(`metric-type`),
      selected: this.attr(`selected`),
      advancedMenuSections: this.attr(`advanced-menu-sections`),
      timePeriod: this.selectedTimePeriodLabel(),
      dataGroupId: this.attr(`data-group-id`),
      dataGroups: this.attr(`data-groups`),
      property: this.state.selectedProperty,
    });
  }

  public selectedTimePeriodLabel() {
    const dateRange = this.attr(`date-range`);
    const chartType = this.attr(`chart-type`);
    const dateUnit = this.attr(`date-unit`);
    return getSelectedTimePeriodLabel(dateRange, chartType, dateUnit);
  }

  public selectedBrowserTooltipLabel() {
    return `Measuring ${this.selectedLabel()}`;
  }

  /** List of selections that will be highlighted in the output copy */
  public getSelections(): string[] {
    return [getPropertyName(this.state.selectedProperty, {includeListItemGroup: true})];
  }

  public selectedRightIcon() {
    // if selector is hovered or the count menu is open, display icon in selector
    if ((this.canOpenMenu() && this.state.isSelectorHovered) || this.attr(`open`)) {
      return `caret-down`;
    }
    return ``;
  }

  public handleLabelMouseEnter() {
    this.update({
      isSelectorHovered: true,
    });
  }

  public handleLabelMouseLeave() {
    this.update({
      isSelectorHovered: false,
    });
  }

  public handleCloseMenu() {
    const {
      countTypeSelections,
      selectedCountTypeIndex,
      selectedProperty,
      selectedAggregationOperatorPerUser,
      selectedAggregationOperator,
      selectedCountUsersOnceType,
    } = this.state;
    const currentCountTypeSelection = countTypeSelections[selectedCountTypeIndex];
    const {countType, canExpandSubSelections: canExpand} = currentCountTypeSelection.value;
    const isSubSelectionsVisible = canExpand({
      countType,
      aggregation: this.state.selectedAggregationOperator,
      countUsersOnceType: this.state.selectedCountUsersOnceType,
    });
    this.update({
      isSubSelectionsVisible,
    });
    const commit = shouldCommitSelection({
      countTypeSelection: currentCountTypeSelection,
      property: selectedProperty,
      aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
      aggregationOperator: selectedAggregationOperator,
      countUsersOnceType: selectedCountUsersOnceType,
    });
    // If the current count type does not have required selections, set the count type selection state back to previous selections
    if (!commit) {
      const {countType, aggregationOperator, aggregationOperatorPerUser, property, countUsersOnceType} = this.attr(
        `selected`,
      );
      this.update({
        selectedCountType: countType,
        selectedCountTypeIndex: countTypeSelections.findIndex((option) => option.value.countType === countType),
        selectedAggregationOperator: aggregationOperator,
        selectedAggregationOperatorPerUser: aggregationOperatorPerUser,
        selectedProperty: property,
        selectedCountUsersOnceType: countUsersOnceType,
      });
    }
  }

  public handleAdvancedMenuChange = (ev: ChangeEvent<AdvancedMenuValue>) => {
    const advancedMenuValue = ev.detail.item.value;
    const {
      selectedCountType,
      selectedProperty,
      selectedAggregationOperator,
      selectedAggregationOperatorPerUser,
    } = this.state;
    const countBy: CountBy = {
      countType: selectedCountType,
      aggregationOperator: selectedAggregationOperator,
      aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
      property: selectedProperty,
      advancedMenuValue,
    };
    this.commitCountBy(countBy);
  };

  public getCountTypeSelections(): Array<SectionItem<CountTypeSelector>> {
    const metricType = this.attr(`metric-type`);
    switch (metricType) {
      case MetricType.Event:
        return INSIGHTS_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.attr(`data-group-id`),
            dataGroups: this.attr(`data-groups`),
          }),
        );
      case MetricType.People:
        return PROFILE_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.attr(`data-group-id`),
            dataGroups: this.attr(`data-groups`),
          }),
        );
      case MetricType.Funnel:
        return FUNNELS_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.attr(`data-group-id`),
            dataGroups: this.attr(`data-groups`),
          }),
        );
      case MetricType.Retention:
        return RETENTION_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.attr(`data-group-id`),
            dataGroups: this.attr(`data-groups`),
          }),
        );
      default:
        return [];
    }
  }

  /**
   * Specifies if the current sub selection can be expanded or not
   * If the previous sub selection has selected value, current sub selection can be expanded
   */
  public canExpandCurrentSubSelection(countTypeIndex: number, previousSelectionType: SubSelectionTypes) {
    if (this.state.selectedCountTypeIndex !== countTypeIndex || !this.state.isSubSelectionsVisible) {
      return false;
    }

    switch (previousSelectionType) {
      case SubSelectionTypes.Property:
        return !!this.state.selectedProperty;
      case SubSelectionTypes.AggregationPerUser:
        // for Aggregation per user, if user property is selected, we do not hide it
        return !!this.state.selectedAggregationOperatorPerUser || !!this.state.selectedProperty;
      case SubSelectionTypes.Aggregation:
        return !!this.state.selectedAggregationOperator;
      default:
        return !!this.state.selectedCountType;
    }
  }

  /** Open sub selection menu */
  public openSubSelectionMenu({
    subSelections,
    subSelectionIndex,
    canExpand = false,
    forceOpen = false,
  }: {
    subSelections: SubSelectionTypes[];
    subSelectionIndex: number;
    canExpand: boolean;
    forceOpen?: boolean;
  }) {
    this.update({
      isSubSelectionsVisible: forceOpen ? true : canExpand,
    });
    const subSelection = subSelections[subSelectionIndex];
    switch (subSelection) {
      case SubSelectionTypes.Property:
        this.update({
          isPropertyMenuOpen: true,
        });
        break;
      case SubSelectionTypes.NestedProperty:
        this.update({
          isNestedPropertyMenuOpen: true,
        });
        break;
      case SubSelectionTypes.AggregationPerUser:
        this.update({
          isAggregationPerUserMenuOpen: true,
        });
        break;
      case SubSelectionTypes.Aggregation:
        this.update({
          isAggregationMenuOpen: true,
        });
        break;
      case SubSelectionTypes.CountUsersOnce:
        this.update({
          isCountUsersOnceMenuOpen: true,
        });
        break;
    }
  }

  public closeAllSubMenus() {
    this.update({
      isPropertyMenuOpen: false,
      isAggregationPerUserMenuOpen: false,
      isNestedPropertyMenuOpen: false,
      isAggregationMenuOpen: false,
      isCountUsersOnceMenuOpen: false,
      isAdvancedMenuOpen: false,
    });
  }

  public disableTooltips() {
    const {
      isPropertyMenuOpen,
      isAggregationPerUserMenuOpen,
      isAggregationMenuOpen,
      isCountUsersOnceMenuOpen,
    } = this.state;
    // disable first level selection (Count Type) tooltips if any of the sub menus are open
    return isPropertyMenuOpen || isAggregationPerUserMenuOpen || isAggregationMenuOpen || isCountUsersOnceMenuOpen;
  }

  /**
   * Handles selecting a caret icon within count option.
   * This will only open the sub selection menu. This will not commit any selections.
   */
  public handleSelectIcon(ev: MouseEvent, selection: SectionItem<CountTypeSelector>, index: number) {
    ev.stopPropagation();

    // De-select existing selections if different options
    if (this.state.selectedCountTypeIndex !== index) {
      this.update({
        selectedAggregationOperatorPerUser: null,
        selectedAggregationOperator: null,
        selectedProperty: null,
      });
    }

    // Open sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection: selection,
      property: this.state.selectedProperty,
    });
    const {countType, canExpandSubSelections} = selection.value;
    const canExpand = canExpandSubSelections({
      countType,
      aggregation: this.state.selectedAggregationOperator,
      countUsersOnceType: this.state.selectedCountUsersOnceType,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: 0,
      canExpand,
      forceOpen: true,
    });

    this.update({
      selectedCountType: selection.value.countType,
      selectedCountTypeIndex: index,
    });
  }

  /**
   * Handles first level selections (Count Types) - i.e. Uniques, Total Events, Sessions
   * @param selection selected count type
   * @param index index of count type within this.state.countTypeSelections
   */
  public handleSelectCountType(selection: SectionItem<CountTypeSelector>, index: number) {
    // De-select existing selections if different options
    if (this.state.selectedCountTypeIndex !== index) {
      this.update({
        selectedAggregationOperatorPerUser: null,
        selectedAggregationOperator: null,
        selectedProperty: null,
      });
    }

    const {countType, operator, property, canExpandSubSelections} = selection.value;
    this.update({
      selectedCountType: countType,
      selectedCountTypeIndex: index,
      selectedProperty: property,
      ...(operator && {
        selectedAggregationOperator: operator,
      }),
    });

    // Open sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection: selection,
      property: this.state.selectedProperty,
    });
    const canExpand = canExpandSubSelections({
      countType,
      aggregation: operator,
      countUsersOnceType: this.state.selectedCountUsersOnceType,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: 0,
      canExpand,
    });

    const commit = shouldCommitSelection({
      countTypeSelection: selection,
      ...(operator && {
        aggregationOperator: operator,
      }),
    });
    if (commit) {
      const countBy: CountBy = {
        countType,
        aggregationOperator: operator,
        advancedMenuValue: this.attr(`selected`).advancedMenuValue,
      };
      this.commitCountBy(countBy);
    }
  }

  /**
   * Handles second level selection (Property) in Sub-selections
   * @param ev Select event from PropertiesMenu
   * @param subSelectionIndex index of SubSelections
   */
  public handleSelectProperty(
    ev: SelectPropertyEvent,
    subSelectionIndex: number,
    {
      isNestedProperty = false,
    }: {
      isNestedProperty?: boolean;
    } = {},
  ) {
    const {rawItem: property} = ev.detail;
    const {
      countTypeSelections,
      selectedCountType,
      selectedCountTypeIndex,
      selectedAggregationOperator,
      selectedAggregationOperatorPerUser,
    } = this.state;
    const countTypeSelection = countTypeSelections[selectedCountTypeIndex];

    const updatedProperty: PropertySectionItem =
      isNestedProperty && isProperty(property)
        ? {
            ...this.state.selectedProperty,
            joinPropertyType: PropertyType.List,
            propertyDefaultType: PropertyType.Object,
            listItemGroup: propertyItemToSegmentation(property, property.resourceType),
          }
        : property;

    this.update({
      selectedProperty: updatedProperty,
    });

    // Open next sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection,
      property: this.state.selectedProperty,
    });
    const canExpand = countTypeSelection.value.canExpandSubSelections({
      countType: selectedCountType,
      aggregation: selectedAggregationOperator,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: subSelectionIndex + 1,
      canExpand,
    });

    const commit = shouldCommitSelection({
      countTypeSelection,
      property: updatedProperty,
      aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
      aggregationOperator: selectedAggregationOperator,
    });
    if (commit) {
      const countBy: CountBy = {
        countType: selectedCountType,
        aggregationOperator: selectedAggregationOperator,
        aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
        property: updatedProperty,
      };
      this.commitCountBy(countBy);
    }
  }

  /**
   * Handles second level selections (AggregationPerUser, Aggregation) in Sub-selections
   * @param aggregation selected aggregation section item from Aggregation Selection
   * @param subSelectionIndex index of SubSelections
   */
  public handleSelectAggregation(aggregation: SectionItem<AggregationSelector>, subSelectionIndex: number) {
    const {operator, perUserOperator, countUsersOnceType} = aggregation.value;
    const {
      countTypeSelections,
      selectedAggregationOperator,
      selectedCountType,
      selectedCountTypeIndex,
      selectedProperty,
      selectedCountUsersOnceType,
    } = this.state;
    const countTypeSelection = countTypeSelections[selectedCountTypeIndex];
    const nextSelectedAggregationOperator = operator || selectedAggregationOperator;
    const nextCountUsersOnceType = Object.values(COUNT_USERS_ONCE_TYPE).some((type) => type === countUsersOnceType)
      ? countUsersOnceType
      : selectedCountUsersOnceType;
    this.update({
      selectedAggregationOperatorPerUser: perUserOperator,
      selectedAggregationOperator: nextSelectedAggregationOperator,
      selectedCountUsersOnceType: nextCountUsersOnceType,
    });

    // Open next sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection,
      property: selectedProperty,
    });
    const canExpand = countTypeSelection.value.canExpandSubSelections({
      countType: selectedCountType,
      aggregation: nextSelectedAggregationOperator,
      countUsersOnceType: nextCountUsersOnceType,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: subSelectionIndex + 1,
      canExpand,
    });

    const commit = shouldCommitSelection({
      countTypeSelection,
      property: selectedProperty,
      aggregationOperatorPerUser: perUserOperator,
      aggregationOperator: nextSelectedAggregationOperator,
      countUsersOnceType: nextCountUsersOnceType,
    });
    if (commit) {
      const countBy: CountBy = {
        countType: selectedCountType,
        aggregationOperator: nextSelectedAggregationOperator,
        aggregationOperatorPerUser: perUserOperator,
        property: selectedProperty,
        countUsersOnceType: nextCountUsersOnceType,
        advancedMenuValue: this.attr(`selected`).advancedMenuValue,
      };
      this.commitCountBy(countBy);
    }
  }

  public commitCountBy(countBy: CountBy) {
    this.closeAllSubMenus();
    const selectEvent = new CustomEvent(`select`, {detail: {item: countBy}});
    this.dispatchEvent(selectEvent);
  }
}

export const CountMenuEl = defineAndMakeJsxFactory<CountMenuJsxProps>(`mp-count-menu`, CountMenu);
export const CountMenuReact = panel2react<CountMenuJsxProps>(`mp-count-menu`, CountMenu);
